// src/components/layout/MobileMenu.jsx
import React, { useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  X,
  Phone,
  MessageCircle,
  ChevronRight,
  Tag,
} from "lucide-react";
import { siteConfig } from "../../homeComponents/data/siteConfig";

const MobileMenu = ({ open, onClose, items }) => {
  // Prevent background scrolling while menu is open
  useEffect(() => {
    if (!open) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="NJV navigation menu"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#061A3A]/50 backdrop-blur-[3px] animate-[fadeIn_.2s_ease-out]"
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        className="
          absolute right-0 top-0 flex h-full w-[88%] max-w-[390px]
          flex-col overflow-hidden bg-white
          shadow-[-12px_0_40px_rgba(6,26,58,0.15)]
          animate-[slideIn_.3s_cubic-bezier(.22,1,.36,1)]
        "
      >
        {/* ================= HEADER ================= */}
        <div className="relative overflow-hidden border-b border-[#E2E8E5]">
          <div className="pointer-events-none absolute -right-12 -top-16 h-40 w-40 rounded-full bg-[#00A260]/10 blur-2xl" />
          <div className="pointer-events-none absolute -left-16 -bottom-20 h-32 w-32 rounded-full bg-[#8BF108]/10 blur-2xl" />

          <div className="relative flex items-center justify-between px-5 py-5">
            {/* Brand */}
            <NavLink
              to="/"
              onClick={onClose}
              className="group flex items-center gap-3"
            >
              <div
                className="
                  flex h-11 w-11 items-center justify-center
                  rounded-2xl bg-[#061A3A]
                  shadow-md transition-transform duration-300
                  group-hover:scale-105
                "
              >
                <span className="text-lg font-black tracking-tight text-white">
                  NJV
                </span>
              </div>

              <div className="flex flex-col leading-none">
                <span className="text-base font-extrabold tracking-tight text-[#061A3A]">
                  NJV
                </span>

                <span className="mt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#475569]">
                  {siteConfig.brand.tagline}
                </span>
              </div>
            </NavLink>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close menu"
              className="
                flex h-10 w-10 items-center justify-center
                rounded-full border border-[#E2E8E5]
                bg-white text-[#061A3A]
                shadow-sm
                transition-all duration-200
                hover:border-[#00A260]
                hover:bg-[#00A260]/5
                hover:text-[#00A260]
                active:scale-95
              "
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* ================= MENU ================= */}
        <div className="flex-1 overflow-y-auto px-4 py-5">
          {/* Section label */}
          <div className="mb-3 px-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#94A3B8]">
              Navigation
            </span>
          </div>

          <nav className="space-y-1.5">
            {items.map((item, index) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                style={{
                  animationDelay: `${index * 45}ms`,
                }}
                className={({ isActive }) =>
                  `
                    group flex items-center justify-between
                    rounded-2xl px-4 py-3.5
                    text-sm font-semibold
                    transition-all duration-200
                    animate-[menuItemIn_.35s_ease-out_both]
                    ${isActive
                    ? "bg-[#00A260]/10 text-[#008F55] shadow-sm"
                    : "text-[#061A3A] hover:bg-[#F1F5F3]"
                  }
                  `
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="flex items-center gap-3">
                      <span
                        className={`
                          h-2 w-2 rounded-full transition-all duration-200
                          ${isActive
                            ? "bg-[#00A260] scale-100"
                            : "scale-0 bg-transparent"
                          }
                        `}
                      />

                      <span>{item.label}</span>
                    </span>

                    <ChevronRight
                      className={`
                        h-4 w-4 transition-all duration-200
                        ${isActive
                          ? "translate-x-0 text-[#00A260]"
                          : "-translate-x-1 text-[#94A3B8] opacity-0 group-hover:translate-x-0 group-hover:opacity-100"
                        }
                      `}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>




          {/* ================= QUICK ACTIONS ================= */}
          <div className="mt-7">
            <div className="mb-3 px-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#94A3B8]">
                Quick Contact
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Call */}
              <a
                href={`tel:${siteConfig.contact.phone}`}
                className="
                  group flex flex-col gap-3
                  rounded-2xl bg-[#00A260]
                  p-4 text-white
                  shadow-md shadow-[#00A260]/20
                  transition-all duration-200
                  hover:-translate-y-0.5
                  hover:bg-[#005543]
                  active:scale-[0.98]
                "
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
                  <Phone className="h-4 w-4" />
                </div>

                <div>
                  <p className="text-[10px] font-medium text-white/70">
                    Call us
                  </p>

                  <p className="mt-0.5 text-sm font-bold">
                    Call NJV
                  </p>
                </div>
              </a>

              {/* WhatsApp */}
              <a
                href={`https://wa.me/${siteConfig.contact.whatsapp.replace(
                  /\D/g,
                  ""
                )}`}
                target="_blank"
                rel="noreferrer"
                className="
                  group flex flex-col gap-3
                  rounded-2xl border border-[#E2E8E5]
                  bg-[#F8FAF9]
                  p-4 text-[#061A3A]
                  transition-all duration-200
                  hover:-translate-y-0.5
                  hover:border-[#00A260]/30
                  hover:bg-[#00A260]/5
                  active:scale-[0.98]
                "
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#00A260]/10 text-[#00A260]">
                  <MessageCircle className="h-4 w-4" />
                </div>

                <div>
                  <p className="text-[10px] font-medium text-[#94A3B8]">
                    Chat with us
                  </p>

                  <p className="mt-0.5 text-sm font-bold">
                    WhatsApp
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* ================= CONTACT INFO ================= */}
          <div className="mt-6 rounded-2xl border border-[#E2E8E5] bg-[#F8FAF9] p-4">
            <p className="text-xs font-bold text-[#061A3A]">
              Need assistance?
            </p>

            <p className="mt-1 text-xs leading-5 text-[#64748B]">
              Our team is available to help you with rides, vehicle
              enquiries, bookings and travel requirements.
            </p>

            {siteConfig.contact.phone && (
              <a
                href={`tel:${siteConfig.contact.phone}`}
                className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#00A260]"
              >
                <Phone className="h-3.5 w-3.5" />
                {siteConfig.contact.phone}
              </a>
            )}
          </div>
        </div>

        {/* ================= FOOTER ================= */}
        <div
          className="
            border-t border-[#E2E8E5]
            bg-white px-5 py-4
          "
          style={{
            paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#94A3B8]">
                NJV
              </p>

              <p className="mt-0.5 text-[10px] text-[#64748B]">
                Travel with confidence.
              </p>
            </div>

            <div className="h-1.5 w-1.5 rounded-full bg-[#00A260]" />
          </div>
        </div>
      </aside>

      {/* Animation keyframes */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @keyframes menuItemIn {
          from {
            opacity: 0;
            transform: translateX(12px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default MobileMenu;